import { FC, useState } from 'react';
import { SummaryTabBlockChildProps } from '../summary-tab/SummaryTab';
import { Encounter } from '@medplum/fhirtypes';
import { usePatient } from '@hooks/usePatient';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Box,
  Card,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
import { ExaminationContent } from './ExaminationContent';
import classes from './Examination.module.css';
import {
  IconCalendar,
  IconChevronRight,
  IconStethoscope,
} from '@tabler/icons-react';
import { useClinicalExaminations } from './useClinicalExaminations';
import { ClinicalExaminationHeader } from './ClinicalExaminationHeader';
import { RecommendationsGeneticTestingTab } from '../current-encounter-tab/RecommendationsGeneticTestingTab';

interface MiniExaminationListProps {
  patient: any;
  onSelect: (encounter: Encounter) => void;
}

//------------------------------------------------------------------------------
const MiniExaminationList: FC<MiniExaminationListProps> = ({
  patient,
  onSelect,
}) => {
  const { t } = useTranslation(['patient-pages']);

  const { currentEncounter } = useCurrentEncounter(patient);
  const { clinicalExaminations } = useClinicalExaminations(patient);

  if (clinicalExaminations.length < 1) {
    return (
      <Text c="dimmed" ta="center" fs="italic">
        {t('examination.notAdded')}
      </Text>
    );
  }

  return (
    <Box>
      {clinicalExaminations.map((en) => (
        <MiniExaminationItem
          key={en.id}
          encounter={en}
          currentEncounter={currentEncounter}
          onClick={() => onSelect(en)}
        />
      ))}
    </Box>
  );
};

//------------------------------------------------------------------------------
interface MiniExaminationItemProps {
  encounter: Encounter;
  currentEncounter?: Encounter;
  onClick?: () => void;
}

//------------------------------------------------------------------------------
const MiniExaminationItem: FC<MiniExaminationItemProps> = ({
  encounter,
  currentEncounter,
  onClick,
}) => {
  // Format the date to be more readable
  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return 'No date';
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error(error);
    }
  };

  // Get the reason for visit
  const reasonForVisit =
    encounter.reasonCode?.[0]?.text ??
    encounter.reasonCode?.[0]?.coding?.[0].display ??
    'No reason specified';

  // Check if this encounter is the current encounter
  const isCurrentEncounter = currentEncounter?.id === encounter.id;

  return (
    <Card
      shadow="sm"
      p="md"
      radius="md"
      m="2px"
      mb="8px"
      withBorder
      bg={isCurrentEncounter ? '#f0fdf4' : undefined}
      className={`${classes.examinationCard} ${onClick ? classes.clickable : ''}`}
      onClick={onClick}
    >
      <Group justify="space-between" wrap="nowrap">
        <Stack gap="xs" className={classes.contentStack}>
          <Group gap="xs" align="center">
            <IconCalendar size={16} className={classes.icon} />
            <Text size="sm" fw={500} c="dimmed">
              {formatDate(encounter.period?.start)}
            </Text>
          </Group>

          <Group gap="xs" align="flex-start">
            <IconStethoscope size={16} className={classes.iconTop} />
            <Text size="sm" lineClamp={2} className={classes.reasonText}>
              {reasonForVisit}
            </Text>
          </Group>
        </Stack>

        {onClick && (
          <ActionIcon variant="subtle" color="gray">
            <IconChevronRight size={16} />
          </ActionIcon>
        )}
      </Group>
    </Card>
  );
};

// -----------------------------------------------------------------------------
const Examination: FC<SummaryTabBlockChildProps> = ({
  minimizedView = false,
}) => {
  const patient = usePatient();
  const { t } = useTranslation(['common', 'patient-pages']);
  //----------------------------------------------------------------------------
  const [selectedEncounter, setSelectedEncounter] = useState<
    Encounter | undefined
  >();
  const [selectedModalOpened, setSelectedModalOpened] = useState(false);

  const { currentEncounter } = useCurrentEncounter(patient);

  //----------------------------------------------------------------------------
  const handleCloseSelectedModal = () => {
    setSelectedEncounter(undefined);
    setSelectedModalOpened(false);
  };

  //----------------------------------------------------------------------------
  if (!patient) {
    return (
      <Text c="dimmed" ta="center" fs="italic">
        {t('loadingMessage', { ns: 'common' })}
      </Text>
    );
  }

  //----------------------------------------------------------------------------
  return (
    <>
      {minimizedView ? (
        <MiniExaminationList
          patient={patient}
          onSelect={(encounter: Encounter) => {
            setSelectedEncounter(encounter);
            setSelectedModalOpened(true);
          }}
        />
      ) : (
        <ExaminationContent patient={patient} encounter={selectedEncounter} />
      )}

      {selectedEncounter && (
        <Modal
          opened={selectedModalOpened}
          onClose={handleCloseSelectedModal}
          size="xl"
          title={
            <Text fw={700} size="xl">
              {t('examination.examination', { ns: 'patient-pages' })}
            </Text>
          }
          closeOnClickOutside={true}
          closeOnEscape={true}
        >
          <Stack>
            <ClinicalExaminationHeader encounter={selectedEncounter} />
            <ExaminationContent
              patient={patient}
              encounter={selectedEncounter}
              readonly={currentEncounter?.id !== selectedEncounter?.id}
            />
            <Divider />
            <Text size="xl" fw={500}>
              {t('currentEncounter.tabs.recommendations', {
                ns: 'patient-pages',
              })}
            </Text>
            <RecommendationsGeneticTestingTab
              patient={patient}
              encounter={selectedEncounter}
              readonly={currentEncounter?.id !== selectedEncounter?.id}
            />
          </Stack>
        </Modal>
      )}
    </>
  );
};

export default Examination;
